import _ from 'lodash';
import React, {memo, useMemo, useState} from 'react';
import {Dropdown} from 'semantic-ui-react';

import {
  OrgType,
  useOrganizationSubscriptionsBasicInfoQuery,
} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {maybePluralize} from '../../../util/uihelpers';
import {accountSettings} from '../../../util/urls';
import {AccountIcon} from './AccountIcon';
import * as S from './AccountPickerSection.styles';
import {useAccountSettingsContext} from './AccountSettingsContextProvider';
import {Account, AccountType, getAccountType} from './util';

const AccountPickerSectionComp = () => {
  const {selectedAccount, loading} = useAccountSettingsContext();

  const accountList = useAccountList();

  if (loading) {
    return <S.LoadingAccountPicker />;
  }

  return (
    <S.SectionWrapper>
      <S.Description>
        You have {maybePluralize(accountList.length, 'account')}.
      </S.Description>
      <AccountDropdown
        accountList={accountList}
        selectedAccount={selectedAccount}
      />
    </S.SectionWrapper>
  );
};

type AccountDropdownProps = {
  accountList: Account[];
  selectedAccount?: Account;
};

const AccountDropdown = ({
  accountList,
  selectedAccount,
}: AccountDropdownProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  if (selectedAccount == null) {
    return null;
  }
  return (
    <Dropdown
      icon={null}
      trigger={
        <AccountItem
          account={selectedAccount}
          isDisplay={true}
          isOpen={menuOpen}
        />
      }
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
      open={menuOpen}>
      <S.AccountDropdownMenu>
        {accountList.map((account, i) => {
          return (
            <React.Fragment key={account.name}>
              {i !== 0 && <S.ItemDivider />}
              <S.DropdownItem>
                <S.AccountLink to={accountSettings(account.name)}>
                  <AccountItem
                    account={account}
                    selected={selectedAccount.name === account.name}
                  />
                </S.AccountLink>
              </S.DropdownItem>
            </React.Fragment>
          );
        })}
      </S.AccountDropdownMenu>
    </Dropdown>
  );
};

type AccountItemProps = {
  account: Account;
  selected?: boolean;
  isDisplay?: boolean;
  isOpen?: boolean;
};

const AccountItem = ({
  account,
  selected = false,
  isDisplay = false,
  isOpen = false,
}: AccountItemProps) => {
  const accountName =
    account.orgType === OrgType.Personal
      ? prettifyPersonalAccountName(account.name)
      : account.name;

  return (
    <S.AccountDropdown isDisplay={isDisplay} isOpen={isOpen}>
      <S.ItemWrapper>
        <AccountIcon accountType={account.accountType} />
        <S.AccountInfoWrapper>
          <S.AccountName>{accountName}</S.AccountName>
          <S.AccountType>{account.accountType}</S.AccountType>
        </S.AccountInfoWrapper>
      </S.ItemWrapper>
      {selected && <S.CheckIcon />}
      {isDisplay && <S.ClosedCarrotIcon />}
    </S.AccountDropdown>
  );
};

export function useDefaultAccountFromAccountList(): Account {
  const viewer = useViewer();
  const accountList = useAccountList();
  const defaultEntityOrganization = viewer?.teams.edges.find(
    org => org.node.id === viewer?.defaultEntity?.id
  );
  const defaultOrganization = accountList.find(
    account =>
      defaultEntityOrganization?.node.organizationId === account.organizationID
  );
  const personalAccount = accountList.find(a => a.orgType === OrgType.Personal);
  const selectedAccount = defaultOrganization ?? personalAccount;

  if (selectedAccount == null) {
    throw new Error(
      'invalid state. account list should always include personal account.'
    );
  }

  return selectedAccount;
}

// returned value accountList should always have the personal account type
// use the above helper function so it throws error when there is no personal account
export function useAccountList(): Account[] {
  const viewer = useViewer();
  const viewerOrgsResult = useOrganizationSubscriptionsBasicInfoQuery({
    fetchPolicy: 'cache-and-network',
  });

  const resultList = useMemo(() => {
    const dummyPersonalAccountData = {
      name: viewer?.username ?? '',
      orgType: OrgType.Personal,
      accountType: AccountType.Personal,
      organizationID: undefined,
    };

    const viewerWithOrgs = viewerOrgsResult.data?.viewer;
    if (viewerOrgsResult.loading || viewerWithOrgs == null) {
      return [dummyPersonalAccountData];
    }

    const unsortedAccountList: Account[] =
      viewerWithOrgs?.organizations?.map(org => {
        const accountType = getAccountType(org);
        return {
          name: org.name,
          orgType: org.orgType,
          accountType,
          organizationID: org.id,
          memberCount: org.members.length,
        };
      }) ?? [];

    const accountList = _.sortBy(unsortedAccountList, 'name');

    // users get personal type organization once they pay for storage plan
    const personalAccount = accountList.find(
      account => account.orgType === OrgType.Personal
    );

    if (personalAccount == null) {
      // if users haven't paid for storage yet, then we need to
      // add a dummy personal account dropdown option to the top of options
      return [dummyPersonalAccountData, ...accountList];
    }

    // if user paid for personal account, move it to the front of the list
    const orgAccountList = accountList.filter(
      account => account.orgType === OrgType.Organization
    );
    return [personalAccount, ...orgAccountList];
  }, [
    viewer?.username,
    viewerOrgsResult.data?.viewer,
    viewerOrgsResult.loading,
  ]);

  return resultList;
}

export const AccountPickerSection = memo(AccountPickerSectionComp);

// helper function to remove random characters from personal organization name
// e.g. input: "username_v1zxctms" => output: "username"
function prettifyPersonalAccountName(name: string): string {
  const splitName = name.split('_'); // => ['username', 'v1zxctms']
  return splitName.length > 0 ? splitName[0] : '';
}
